import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApprovalDecision, InboxEntry } from '@this+that/models';
import log from 'loglevel';

import {
  BRAIN_REVIEW_DECISION_BY_APPROVAL,
  BRAIN_REVIEW_STATE_BY_APPROVAL,
  resolveBrainReviewErrorKey,
} from './brainReviewErrorKey';
import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useRespondToBrainReviewService } from '../../services/companyBrain.service';
import { useUnreadStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

const SUCCESS_TOAST_KEY: Record<ApprovalDecision, string> = {
  [ApprovalDecision.APPROVED]: 'doBox.brainReview.approved',
  [ApprovalDecision.REJECTED]: 'doBox.brainReview.rejected',
};

const ALREADY_DECIDED_BY_OTHER_KEY = 'doBox.brainReview.alreadyDecidedByOther';
const RESPOND_ERROR_KEY = 'doBox.brainReview.respondError';

export const useRespondToBrainReview = () => {
  const { t } = useTranslation();
  const { respondToBrainReview: respondService } =
    useRespondToBrainReviewService();
  const { removeInboxEntry } = useActiveInboxEntriesSlice();
  const { markAsRead } = useUnreadStore();
  const { successMessage, warningMessage, errorMessage } = useCustomToast();
  const [isResponding, setIsResponding] = useState(false);

  const respondToBrainReview = useCallback(
    async (entry: InboxEntry, decision: ApprovalDecision): Promise<boolean> => {
      const ref = entry.brainReview;
      if (!ref) {
        errorMessage(t(RESPOND_ERROR_KEY));
        return false;
      }

      setIsResponding(true);
      try {
        // An already-decided review comes back settled, not as an error; the service's cache write refreshes the card.
        const settled = await respondService(
          ref.teamId,
          ref.assertionId,
          BRAIN_REVIEW_DECISION_BY_APPROVAL[decision],
        );
        if (settled.reviewState !== BRAIN_REVIEW_STATE_BY_APPROVAL[decision]) {
          warningMessage(t(ALREADY_DECIDED_BY_OTHER_KEY));
          return false;
        }
        removeInboxEntry(entry.id);
        markAsRead(entry.id, entry.taskGroupId);
        successMessage(t(SUCCESS_TOAST_KEY[decision]));
        return true;
      } catch (error) {
        log.error('respondToBrainReview error', {
          entryId: entry.id,
          decision,
          error,
        });
        errorMessage(t(resolveBrainReviewErrorKey(error)));
        return false;
      } finally {
        setIsResponding(false);
      }
    },
    [
      respondService,
      removeInboxEntry,
      markAsRead,
      successMessage,
      warningMessage,
      errorMessage,
      t,
    ],
  );

  return { respondToBrainReview, isResponding };
};
